"use client";

import { gsap } from "gsap";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { Calendar, Star } from "lucide-react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const TechHeroSection = ({ years = [], totalEvents = 0 }) => {
  const root = useRef(null);
  const imageRef = useRef(null);

  const minYear = years.length ? Math.min(...years) : 2023;
  const maxYear = years.length ? Math.max(...years) : 2026;

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.from(".hero-eyebrow", {
        y: 30,
        opacity: 0,
        duration: 0.8,
      })
        .from(
          ".hero-line",
          {
            y: 80,
            opacity: 0,
            duration: 1.1,
            stagger: 0.12,
          },
          "-=0.5",
        )
        .from(
          ".hero-sub",
          {
            y: 40,
            opacity: 0,
            duration: 1,
          },
          "-=0.6",
        )
        .from(
          ".hero-pill",
          {
            scale: 0.85,
            opacity: 0,
            duration: 0.6,
            stagger: 0.1,
          },
          "-=0.7",
        );

      gsap.to(imageRef.current, {
        yPercent: 18,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="relative hero-section w-full overflow-hidden bg-background-primary !px-4">
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px] dark:hidden">
        <Image src="/images/serviceBanners/globalWhite.webp" alt="Global Events Map Banner" fill priority className="object-cover " />
      </div>

      <div className="relative hidden w-full h-[260px] sm:h-[340px] dark:block lg:dark:hidden">
        <Image src="/images/serviceBanners/globlaBlack.webp" alt="Global Events Map Banner" fill priority className="object-cover " />
      </div>

      <div ref={imageRef} className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image src="/images/serviceBanners/globalWhite.webp" alt="Global Events Map Banner" fill priority className="object-contain object-right dark:hidden" />
        <Image src="/images/serviceBanners/globlaBlack.webp" alt="Global Events Map Banner" fill priority className="object-contain object-right hidden dark:block" />
      </div>

      <div
        className="absolute inset-0 z-10 hidden lg:block dark:hidden"
        style={{
          background: "linear-gradient(90deg, rgba(240, 242, 255, 1) 15%, transparent 100%)",
        }}
      />

      <div
        className="absolute inset-0 z-10 hidden dark:lg:block"
        style={{
          background: "linear-gradient(90deg, #0d0f1e 0%, #0d0f1e 30%, transparent 100%)",
        }}
      />

      <div className="relative z-20 flex flex-col justify-center  px-6! !py-10">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-4! hero-eyebrow">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
              GLOBAL TECH EVENTS
            </h3>

            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="hero-line text-text-primary block">
              Every <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Year.</span>
            </span>

            <span className="hero-line text-text-primary block">
              Every <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Stage.</span>
            </span>
          </h1>

          <p className="hero-sub text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            Relive the journey of innovation through global summits, major expos, and industry events across the evolving technology and Web3 ecosystem. We highlight experiences
            that reflect continuous learning, breakthrough ideas, and meaningful collaboration. These moments showcase our active participation in shaping digital transformation,
            advancing technology, and contributing to impactful milestones that define the future of modern industries and global innovation.
          </p>

          <div className="flex flex-col sm:flex-row !gap-4">
            <div
              className="flex items-center !gap-4 border border-border-primary rounded-2xl !px-4 !py-4 w-[180px]"
              style={{
                background: "var(--card-background)",
                boxShadow: "var(--card-shadow)",
              }}
            >
              <Calendar className="w-10 h-10 text-[#a855f7]" />
              <div>
                <div className="text-text-primary font-light text-[14px]">
                  {minYear} → {maxYear}
                </div>
                <div className="text-text-secondary text-[13px]">{years.length} Years</div>
              </div>
            </div>

            <div
              className="flex items-center !gap-4 border border-border-primary rounded-2xl !px-4 !py-4 w-[160px]"
              style={{
                background: "var(--card-background)",
                boxShadow: "var(--card-shadow)",
              }}
            >
              <Star className="w-8 h-8 text-[#a855f7]" />
              <div>
                <div className="text-text-primary font-light text-[17px]">{totalEvents}</div>
                <div className="text-text-secondary text-[13px]">Events</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default TechHeroSection;
